import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Location } from 'history';
import moment from "moment";
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import { callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb } from 'common/appInterface';

const NonPaymentFeeListPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "TRAN04";
  const screenName = t("TRAN04.title");

  const [nonInoutStartDate, setNonInoutStartDate] = useState(moment().add(-12, "months").format("YYYY-MM-DD"));
  const [nonInoutEndDate, setNonInoutEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [key, setKey] = useState("3");

  const [nonInfoList, setNonInfoList] = useState<NonInoutInfo[]>([]);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 미납내역검색 */
  const nonInoutInfoSearch = useCallback(async (nonInoutStartDate: string, nonInoutEndDate: string) => {
    if (key === "4") {
      if (nonInoutEndDate < nonInoutStartDate) {
        return;
      }
    }

    showProgressWeb();
    await client.get("/api/app/approval/selectNonInoutList", { params: { ...location.state.query,  alternativeCardNo: location.state.info.alternativeCardNo, nonInoutStartDate: nonInoutStartDate, nonInoutEndDate: nonInoutEndDate }}).then((response) => {
      let result = response.data.response;
      setNonInfoList(result);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location, key]);

  /** 1개월 검색 */
  const oneMonthSearch = useCallback(() => {
    setKey("1");

    let startDate = moment().add(-1, "months").format("YYYY-MM-DD");
    let endDate = moment().format("YYYY-MM-DD");
    nonInoutInfoSearch(startDate, endDate);
  }, [nonInoutInfoSearch]);

  /** 3개월 검색 */
  const threeMonthSearch = useCallback(() => {
    setKey("2");

    let startDate = moment().add(-3, "months").format("YYYY-MM-DD");
    let endDate = moment().format("YYYY-MM-DD");
    nonInoutInfoSearch(startDate, endDate);
  }, [nonInoutInfoSearch]);

  /** 12개월 검색 */
  const oneYearSearch = useCallback(() => {
    setKey("3");
    
    let startDate = moment().add(-12, "months").format("YYYY-MM-DD");
    let endDate = moment().format("YYYY-MM-DD");
    nonInoutInfoSearch(startDate, endDate);
  }, [nonInoutInfoSearch]);

  /** 기간조회 */
  const termSearch = useCallback(() => {
    setKey("4");
  }, []);

  /** DatePicker 읽기전용 처리 */
  const onDatePickerRef = ((el: any) => {
    if (el && el.input) {
      el.input.readOnly = true;
    }
  })

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state.info);

    oneYearSearch();

    // BODY클래스 추가
    document.body.classList.add("bgColorTy01");

    return () => {
      // BODY클래스 제거
      document.body.classList.remove("bgColorTy01");
    }
    
    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden mycardhistory bgColorTy01">
        <div className="inner">
          <div className="optionWrap">
            <ul className={key === "4" ? "optionList setTerm on" : "optionList setTerm"}>
              <li className="option">
                <input type="radio" id="opt01" name="opt" checked={key === "1" ? true : false} readOnly />
                <label htmlFor="opt01">
                  <span onClick={oneMonthSearch}>{t("TRAN04.button01")}</span>
                </label>
              </li>
              <li className="option">
                <input type="radio" id="opt02" name="opt" checked={key === "2" ? true : false} readOnly />
                <label htmlFor="opt02">
                  <span onClick={threeMonthSearch}>{t("TRAN04.button02")}</span>
                </label>
              </li>
              <li className="option">
                <input type="radio" id="opt03" name="opt" checked={key === "3" ? true : false} readOnly />
                <label htmlFor="opt03">
                  <span onClick={oneYearSearch}>{t("TRAN04.button03")}</span>
                </label>
              </li>
              <li className="option set">
                <input type="radio" id="opt04" name="opt" checked={key === "4" ? true : false} readOnly />
                <label htmlFor="opt04">
                  <span onClick={termSearch}>{t("TRAN04.button04")}</span>
                </label>
              </li>
            </ul>
            <div className="setDetail">
              <div className="calenderWrap wide">
                <div className="controller">
                  <div className="inputBoxTyAD01">
                    <DatePicker onChange={(date) => setNonInoutStartDate(stringUtils.dateFormat(date,'yyyy-MM-dd'))} dateFormat="yyyy.MM.dd" selected={moment(nonInoutStartDate).toDate()} maxDate={moment(nonInoutEndDate).toDate()} showPopperArrow={false} popperModifiers={[{ name: "preventOverflow", options: { altAxis: true }}]} dateFormatCalendar={"yyyy.MM"} ref={onDatePickerRef}></DatePicker>
                  </div>
                  <span>~</span>
                  <div className="inputBoxTyAD01">
                    <DatePicker onChange={(date) => setNonInoutEndDate(stringUtils.dateFormat(date,'yyyy-MM-dd'))} dateFormat="yyyy.MM.dd" selected={moment(nonInoutEndDate).toDate()} minDate={moment(nonInoutStartDate).toDate()} showPopperArrow={false} popperModifiers={[{ name: "preventOverflow", options: { altAxis: true }}]} dateFormatCalendar={"yyyy.MM"} ref={onDatePickerRef}></DatePicker>
                  </div>
                </div>
              </div>
              {/*<p className="msg">{t("TRAN04.explan01")}</p>*/}
              <button type="button" className="btnPrimaryTy01 btnsm" onClick={() => nonInoutInfoSearch(nonInoutStartDate, nonInoutEndDate)}><span>{t("TRAN04.button05")}</span></button>
            </div>
          </div>
        </div>
        <section className="contents unpayWrap pb64-mb">
          <div className="inner">
            <div className="settingListWrap clfix">
              <dl className="settingList">
                {nonInfoList.map((item, index) =>
                  <React.Fragment key={item.nonInoutSeq}>
                    {(index === 0 || nonInfoList[index -1].nonInoutRegisteredDate.substring(0, 4) !== nonInfoList[index].nonInoutRegisteredDate.substring(0, 4)) && <dt><span className="day">{item.nonInoutRegisteredDate.substring(0, 4)}</span></dt>}
                    <dd>
                      <button type="button">
                        <span className="spot">{item.nonInoutRegisteredDate.substring(0, 4) + "년 " + item.nonInoutRegisteredDate.substring(4, 6) + "월"}</span>
                        <p className="date">
                          {t("TRAN04.explan02")} ({item.cardProductCodeName}-{item.cardNo})
                        </p>
                        <span className="unpaid">{stringUtils.moneyFormat(item.nonInoutOriginAmount)} KRW</span>
                      </button>
                    </dd>
                  </React.Fragment>
                )}
              </dl>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default NonPaymentFeeListPage;


